import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search, Edit, Trash2, Fish, Loader2, FileCheck } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";

const initialFormData = {
  nombre: "",
  presentacion: "",
  empaque: "",
  empaque2: "",
  empaque3: "",
  empaque4: "",
  temperatura_almacenaje: "Mantener congelado a -18º C",
  nombre_cientifico: "",
  zona_fao: "",
  arte_pesca: "",
  codigo_alfa: "",
  pais_origen: "Venezuela",
  puerto_desembarco: "",
  codigo_arancelario: "",
  precio_coste: 0,
  precio_venta: 0,
  alergenos: "",
  requiere_anexo_iv: true,  // Por defecto, todos los productos requieren Anexo IV
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const { hasRole } = useAuth();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar los productos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingId(product.id);
      setFormData(product);
    } else {
      setEditingId(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error("El nombre del producto es obligatorio");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, formData);
        toast.success("Producto actualizado correctamente");
      } else {
        await api.post("/products", formData);
        toast.success("Producto creado correctamente");
      }
      fetchProducts();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el producto");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success("Producto eliminado correctamente");
      setProducts(products.filter((p) => p.id !== deleteId));
    } catch (error) {
      toast.error("Error al eliminar el producto");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredProducts = products.filter(
    (product) =>
      product.nombre?.toLowerCase().includes(search.toLowerCase()) ||
      product.nombre_cientifico?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Productos</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500">Catálogo de productos pesqueros</p>
            {hasRole("cliente") && (
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">
                Consultar precio final al realizar el pedido, precios de referencia. Precios sin IVA
              </span>
            )}
          </div>
        </div>
        {hasRole("admin") && (
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="create-product-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Producto
          </Button>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar por nombre o nombre científico..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-products"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Fish className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay productos</h3>
          <p className="text-slate-500 mb-4">
            {search ? "No se encontraron productos con ese criterio" : "Comienza creando tu primer producto"}
          </p>
          {hasRole("admin") && !search && (
            <Button onClick={() => handleOpenDialog()} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Crear Producto
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Producto
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Nombre Científico
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Zona FAO
                </TableHead>
                <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                  Origen
                </TableHead>
                {/* Precio Coste: solo admin y proveedor */}
                {(hasRole("admin") || hasRole("proveedor")) && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                    Precio Coste
                  </TableHead>
                )}
                {/* Precio Venta: admin y cliente */}
                {(hasRole("admin") || hasRole("cliente")) && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider">
                    Precio Venta
                  </TableHead>
                )}
                {hasRole("admin") && (
                  <TableHead className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider w-[100px]">
                    Acciones
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} className="hover:bg-slate-50" data-testid={`product-row-${product.id}`}>
                  <TableCell className="font-medium">{product.nombre}</TableCell>
                  <TableCell className="italic text-slate-500">{product.nombre_cientifico || "-"}</TableCell>
                  <TableCell>{product.zona_fao || "-"}</TableCell>
                  <TableCell>{product.pais_origen || "-"}</TableCell>
                  {/* Precio Coste: solo admin y proveedor */}
                  {(hasRole("admin") || hasRole("proveedor")) && (
                    <TableCell className="font-mono">
                      {product.precio_coste ? `$${product.precio_coste.toFixed(2)}` : "-"}
                    </TableCell>
                  )}
                  {/* Precio Venta: admin y cliente */}
                  {(hasRole("admin") || hasRole("cliente")) && (
                    <TableCell className="font-mono">
                      {product.precio_venta ? `$${product.precio_venta.toFixed(2)}` : "-"}
                    </TableCell>
                  )}
                  {hasRole("admin") && (
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(product)}
                          data-testid={`edit-product-${product.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(product.id)}
                          data-testid={`delete-product-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: PULPO CONGELADO"
                  required
                  data-testid="input-product-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre Científico</Label>
                <Input
                  value={formData.nombre_cientifico}
                  onChange={(e) => setFormData({ ...formData, nombre_cientifico: e.target.value })}
                  placeholder="Ej: OCTOPUS VULGARIS"
                  data-testid="input-product-cientifico"
                />
              </div>
              <div className="space-y-2">
                <Label>Presentación</Label>
                <Input
                  value={formData.presentacion}
                  onChange={(e) => setFormData({ ...formData, presentacion: e.target.value })}
                  placeholder="Ej: Sin vísceras, con pico y ojo"
                />
              </div>
              <div className="space-y-2">
                <Label>Zona FAO</Label>
                <Input
                  value={formData.zona_fao}
                  onChange={(e) => setFormData({ ...formData, zona_fao: e.target.value })}
                  placeholder="Ej: Zona FAO31"
                />
              </div>
              <div className="space-y-2">
                <Label>Arte de Pesca</Label>
                <Input
                  value={formData.arte_pesca}
                  onChange={(e) => setFormData({ ...formData, arte_pesca: e.target.value })}
                  placeholder="Ej: Nasas (FPO)"
                />
              </div>
              <div className="space-y-2">
                <Label>País de Origen</Label>
                <Input
                  value={formData.pais_origen}
                  onChange={(e) => setFormData({ ...formData, pais_origen: e.target.value })}
                  placeholder="Venezuela"
                />
              </div>
              <div className="space-y-2">
                <Label>Puerto de Desembarco</Label>
                <Input
                  value={formData.puerto_desembarco}
                  onChange={(e) => setFormData({ ...formData, puerto_desembarco: e.target.value })}
                  placeholder="Ej: Nueva Esparta, Isla de Margarita"
                />
              </div>
              <div className="space-y-2">
                <Label>Código Alfa</Label>
                <Input
                  value={formData.codigo_alfa}
                  onChange={(e) => setFormData({ ...formData, codigo_alfa: e.target.value })}
                  placeholder="Ej: OCC"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Código Arancelario</Label>
                <Input
                  value={formData.codigo_arancelario}
                  onChange={(e) => setFormData({ ...formData, codigo_arancelario: e.target.value })}
                  placeholder="Ej: 0307.52.00.00"
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>Precio Coste ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.precio_coste}
                  onChange={(e) => setFormData({ ...formData, precio_coste: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                  placeholder="USD"
                />
              </div>
              <div className="space-y-2">
                <Label>Precio Venta (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.precio_venta}
                  onChange={(e) => setFormData({ ...formData, precio_venta: parseFloat(e.target.value) || 0 })}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Temperatura de Almacenaje</Label>
                <Input
                  value={formData.temperatura_almacenaje}
                  onChange={(e) => setFormData({ ...formData, temperatura_almacenaje: e.target.value })}
                  placeholder="Mantener congelado a -18º C"
                />
              </div>
              <div className="space-y-2">
                <Label>Empaque 1 *</Label>
                <Input
                  value={formData.empaque}
                  onChange={(e) => setFormData({ ...formData, empaque: e.target.value })}
                  placeholder="Ej: BLOQUE"
                  data-testid="input-product-empaque"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Empaque 2</Label>
                <Input
                  value={formData.empaque2}
                  onChange={(e) => setFormData({ ...formData, empaque2: e.target.value })}
                  placeholder="Ej: IQF (opcional)"
                />
              </div>
              <div className="space-y-2">
                <Label>Empaque 3</Label>
                <Input
                  value={formData.empaque3}
                  onChange={(e) => setFormData({ ...formData, empaque3: e.target.value })}
                  placeholder="Ej: FILETE (opcional)"
                />
              </div>
              <div className="space-y-2">
                <Label>Empaque 4</Label>
                <Input
                  value={formData.empaque4}
                  onChange={(e) => setFormData({ ...formData, empaque4: e.target.value })}
                  placeholder="Ej: CAVA (opcional)"
                />
              </div>
              <div className="space-y-2">
                <Label>Alérgenos</Label>
                <Input
                  value={formData.alergenos}
                  onChange={(e) => setFormData({ ...formData, alergenos: e.target.value })}
                  placeholder="Ej: Moluscos, Crustáceos"
                />
              </div>
              
              {/* Checkbox Anexo IV */}
              <div className="col-span-2 flex items-center space-x-2 pt-2 border-t">
                <Checkbox
                  id="requiere_anexo_iv"
                  checked={formData.requiere_anexo_iv !== false}
                  onCheckedChange={(checked) => setFormData({ ...formData, requiere_anexo_iv: checked })}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label 
                    htmlFor="requiere_anexo_iv" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1"
                  >
                    <FileCheck className="w-4 h-4 text-blue-600" />
                    Requiere Anexo IV
                  </Label>
                  <p className="text-xs text-gray-500">
                    Si está marcado, el documento "Anexo IV" será obligatorio para enviar la documentación del lote
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={saving} data-testid="save-product-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingId ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el producto.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
